import { useEffect, useRef, useState } from 'react';
import { X, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/api/users';
import Avatar from './Avatar';
import clsx from 'clsx';

/**
 * Phase 13 — переиспользуемый picker юзеров по username/displayName.
 *
 * Раньше workspace-формы требовали ручной ввод cuid (`cm…`); теперь админ
 * пишет 2-3 буквы → выпадает дропдаун → клик. Использует существующий
 * `GET /api/users/search` (`backend/src/routes/user.routes.ts:33-41`),
 * который возвращает {id, username, displayName, avatarUrl, status}
 * и автоматически фильтрует BANNED.
 *
 * Поведение:
 *   - debounce 250ms между keystroke и запросом
 *   - min 2 chars
 *   - excludeUserIds — IDs, которые не показывать (уже добавленные members)
 *   - selected → pill-режим, X очищает
 *   - Esc / клик мимо → закрыть дропдаун
 */
export interface PickedUser {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  status?: 'ONLINE' | 'AWAY' | 'DND' | 'OFFLINE';
}

export interface UserPickerProps {
  value: PickedUser | null;
  onChange: (user: PickedUser | null) => void;
  placeholder?: string;
  excludeUserIds?: string[];
  disabled?: boolean;
  /** Optional id for accessibility (label htmlFor). */
  id?: string;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function UserPicker({
  value, onChange, placeholder = 'Имя или @username…',
  excludeUserIds, disabled, id,
}: UserPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 250);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['user-picker', debouncedQuery],
    queryFn: () => usersApi.search(debouncedQuery, 10),
    enabled: open && debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
  });

  // Filter out excluded
  const filtered = excludeUserIds && excludeUserIds.length > 0
    ? results.filter((u) => !excludeUserIds.includes(u.id))
    : results;

  // Click outside / Esc → close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Selected pill view
  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg">
        <Avatar src={value.avatarUrl} username={value.username} size="sm" />
        <div className="flex-1 min-w-0">
          <p className="text-surface-100 text-sm truncate">{value.displayName}</p>
          <p className="text-surface-500 text-xs truncate">@{value.username}</p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => { onChange(null); setQuery(''); requestAnimationFrame(() => inputRef.current?.focus()); }}
            className="p-1 text-surface-500 hover:text-red-400 transition-colors"
            title="Очистить"
          >
            <X size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500 pointer-events-none"
        />
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="irc-input pl-9"
        />
      </div>
      {open && debouncedQuery.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-surface-900 border border-surface-700 rounded-lg shadow-xl max-h-72 overflow-y-auto">
          {isFetching && filtered.length === 0 ? (
            <div className="px-3 py-2 text-surface-500 text-sm">Поиск…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-surface-500 text-sm">Никого не найдено</div>
          ) : (
            <ul>
              {filtered.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange({
                        id: u.id, username: u.username, displayName: u.displayName,
                        avatarUrl: u.avatarUrl, status: u.status,
                      });
                      setOpen(false);
                      setQuery('');
                    }}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-2 text-left',
                      'hover:bg-surface-800 transition-colors',
                    )}
                  >
                    <Avatar src={u.avatarUrl} username={u.username} size="sm" status={u.status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-surface-100 text-sm truncate">{u.displayName}</p>
                      <p className="text-surface-500 text-xs truncate">@{u.username}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
